import { useState } from 'react';

import Modal from '@/components/Modal/Modal';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import Bookmark, { type BookmarkModalState } from '@/features/Bookmark/Bookmark';
import BookmarkLinkImage from '@/features/Bookmark/BookmarkLinkImage';
import BookmarkCreateModal from '@/features/Bookmark/BookmarkModal/BookmarkCreateModal';
import IconTextProperty from '@/features/Commons/Label/IconTextProperty';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import { isEmptyArray } from '@/utils/util';

import { useFeedbackDetailContext } from '../../context/FeedbackDetailContext';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';

export const BookmarkField = (
  props: CommonFieldProps & {
    feedback_alias: string;
    bookmarks: BookmarkT[];
  },
) => {
  const { updateDetail } = useFeedbackDetailContext();
  const { editBookmark, removeBookmark, addBookmark } = useFeedbackListStore(
    (state) => ({
      editBookmark: state.editBookmark,
      removeBookmark: state.removeBookmark,
      addBookmark: state.addBookmark,
    }),
  );
  const [modalState, setModalState] = useState<BookmarkModalState>({
    status: false,
    action: 'add',
    target: '',
  });

  const selectedBookmark = props.bookmarks.find(
    (bookmark) => bookmark.alias === modalState.target,
  );

  if (!props.showFull && isEmptyArray(props.bookmarks)) return null;

  if (props.isStatic)
    return (
      <PropertyRowWrapper {...props} expandable>
        <PropertyValue
          value={
            props.bookmarks ? (
              props.bookmarks.map((bookmark) => {
                return (
                  <IconTextProperty
                    key={bookmark.name}
                    value={bookmark.name}
                    icon={<BookmarkLinkImage link={bookmark.link} />}
                  />
                );
              })
            ) : (
              <Typo type="label3">{`-`}</Typo>
            )
          }
        />
      </PropertyRowWrapper>
    );

  return (
    <PropertyRowWrapper {...props} expandable>
      <Bookmark
        bookmarkList={props.bookmarks}
        setBookmarkModalStatus={async (status) => {
          if (status.action === 'delete') {
            const newBookmarkList = await removeBookmark(
              props.feedback_alias,
              status.target,
            );
            updateDetail({
              bookmarks: newBookmarkList,
            });
          } else {
            setModalState({
              status: status.status,
              action: status.action,
              target: status.target,
            });
          }
        }}
      />
      <Modal
        status={modalState.status && modalState.action !== 'delete'}
        closeModal={() =>
          setModalState({
            status: false,
            action: 'add',
            target: '',
          })
        }
      >
        <BookmarkCreateModal
          type="feedback"
          link={selectedBookmark?.link}
          name={selectedBookmark?.name}
          isEdit={modalState.action === 'edit'}
          onClick={async (bookmark: PostBookmarkParams) => {
            if (modalState.action === 'edit') {
              const newBookmark = await editBookmark(
                props.feedback_alias,
                modalState.target || '',
                bookmark,
              );
              updateDetail({
                bookmarks: props.bookmarks.map((b) =>
                  b.alias === newBookmark.alias ? newBookmark : b,
                ),
              });
            } else {
              const newBookmark = await addBookmark(
                props.feedback_alias,
                bookmark,
              );
              updateDetail({
                bookmarks: [...props.bookmarks, newBookmark],
              });
            }
          }}
        />
      </Modal>
    </PropertyRowWrapper>
  );
};
